/**
 * Pagination state hook.
 * 
 * @module hooks/usePagination
 */

import { useState, useCallback, useMemo } from 'react';

interface UsePaginationOptions {
  initialPage?: number;
  initialPageSize?: number;
  pageSizeOptions?: number[];
}

interface UsePaginationReturn {
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  changePageSize: (size: number) => void;
  reset: () => void;
  /** Get pagination parameters for API calls */
  paginationParams: {
    page: number;
    pageSize: number;
    from: number;
    to: number;
  };
}

/**
 * Manages pagination state for lists.
 * 
 * @param options - Pagination options
 * @returns Pagination state and controls
 * 
 * @example
 * const { page, pageSize, goToPage, paginationParams } = usePagination({
 *   initialPageSize: 25
 * });
 */
export function usePagination(options: UsePaginationOptions = {}): UsePaginationReturn {
  const {
    initialPage = 1,
    initialPageSize = 25,
    pageSizeOptions = [10, 25, 50, 100],
  } = options;

  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const goToPage = useCallback((newPage: number) => {
    setPage(Math.max(1, newPage));
  }, []);

  const nextPage = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const prevPage = useCallback(() => {
    setPage((prev) => Math.max(1, prev - 1));
  }, []);

  const changePageSize = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  }, []);

  const reset = useCallback(() => {
    setPage(initialPage);
    setPageSize(initialPageSize);
  }, [initialPage, initialPageSize]);

  const paginationParams = useMemo(() => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    return { page, pageSize, from, to };
  }, [page, pageSize]);

  return {
    page,
    pageSize,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    reset,
    paginationParams,
  };
}
